import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface GitFile { status: string; path: string }
interface Commit { hash: string; message: string; author: string; date: string }

export function GitPanel() {
  const [initialized, setInitialized] = useState(false)
  const [branch, setBranch] = useState('')
  const [files, setFiles] = useState<GitFile[]>([])
  const [clean, setClean] = useState(true)
  const [commits, setCommits] = useState<Commit[]>([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [committing, setCommitting] = useState(false)
  const [showLog, setShowLog] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/git/status'))
      const data = await res.json()
      setInitialized(data.initialized)
      setBranch(data.branch ?? '')
      setFiles(data.files ?? [])
      setClean(data.clean ?? true)
    } catch {}

    try {
      const res = await authFetch(apiUrl('/api/git/log'))
      setCommits(await res.json())
    } catch {}

    setLoading(false)
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const handleInit = async () => {
    await authFetch(apiUrl('/api/git/init'), { method: 'POST' })
    refresh()
  }

  const handleCommit = async () => {
    if (!message.trim()) return
    setCommitting(true)
    await authFetch(apiUrl('/api/git/commit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: message.trim() }),
    })
    setMessage('')
    setCommitting(false)
    refresh()
  }

  const statusColor: Record<string, string> = {
    M: 'var(--accent-3)', A: 'var(--accent-2)', D: 'var(--red)',
    '?': 'var(--text-2)', U: 'var(--accent)',
  }

  const statusLabel: Record<string, string> = {
    M: 'Modified', A: 'Added', D: 'Deleted', '?': 'Untracked', '??': 'Untracked', U: 'Unmerged',
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>SOURCE CONTROL</span>
        <button onClick={refresh} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 14, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>Loading...</div>
      ) : !initialized ? (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
            No git repository found in workspace
          </div>
          <button onClick={handleInit} style={btnStyle}>Initialize Repository</button>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Branch */}
          <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--border)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>
            <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>{branch}</span>
            {clean && <span style={{ fontSize: 9, color: 'var(--accent-2)', marginLeft: 'auto' }}>clean</span>}
          </div>

          {/* Commit form */}
          {files.length > 0 && (
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCommit() }}
                placeholder="Commit message..."
                style={{
                  width: '100%', padding: '6px 10px', borderRadius: 6,
                  border: '1px solid var(--border-2)', background: 'var(--bg-2)',
                  color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none',
                  marginBottom: 8,
                }}
              />
              <button onClick={handleCommit} disabled={committing || !message.trim()} style={{ ...btnStyle, width: '100%', opacity: message.trim() ? 1 : 0.4 }}>
                {committing ? 'Committing...' : `Commit ${files.length} file${files.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}

          {/* Changed files */}
          {files.length > 0 && (
            <div>
              <div style={{ padding: '6px 14px', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Changes ({files.length})
              </div>
              {files.map((f) => (
                <div key={f.path} style={{
                  padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-1)',
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, minWidth: 14, textAlign: 'center',
                    color: statusColor[f.status] ?? 'var(--text-2)',
                  }}>{f.status}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.path}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-2)' }}>{statusLabel[f.status] ?? f.status}</span>
                </div>
              ))}
            </div>
          )}

          {/* Commit log toggle */}
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 8 }}>
            <button
              onClick={() => setShowLog((s) => !s)}
              style={{ width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              <span>{showLog ? '▾' : '▸'}</span>
              Commits ({commits.length})
            </button>
            {showLog && commits.map((c) => (
              <div key={c.hash} style={{
                padding: '5px 14px 5px 24px', fontSize: 11, fontFamily: 'var(--font-mono)',
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 700 }}>{c.hash}</span>
                  <span style={{ color: 'var(--text-0)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</span>
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', marginTop: 2 }}>{c.author} - {c.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--accent)',
  background: 'rgba(124,110,245,0.1)', color: 'var(--accent)',
  cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
  transition: 'all 0.12s',
}
